import { TabContext } from '@mui/lab';
import { ChangeEvent, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useTranslate, useGetMany } from 'react-admin';
import {
  CategoryRecord,
  HotelRecord,
  ItemRecordLocalized,
  Timeframe,
} from '../../../common/types';
import UsageTotalsKeenChart from '../UsageTotalsKeenChart';
import { useIntl } from '../../../context/intlContext';
import {
  useOrderByCategoryData,
  useOrderHourlyAverageData,
  useOrderOrderItemsByCategoryData,
  useOrderTotalsData,
  useOrderWeekdayAverageData,
} from '../data-hooks';
import ChartTab from '../shared/ChartTab';
import ChartListTab from '../shared/ChartListTab';
import ChartsCircularProgress from '../shared/ChartsCircularProgress';
import ChartListTabList from '../shared/ChartListTabList';
import ChartTabPanel from '../shared/ChartTabPanel';
import ChartList from '../shared/ChartList';
import ChartMainTabList from '../shared/ChartMainTabList';
import ChartTooltip from '../shared/ChartTooltip';
import ListItemProgressBar from '../shared/ListItemProgressBar';
import ChartContainer from '../shared/ChartContainer';
import ChartSideList from '../shared/ChartSideList';
import ChartContent from '../shared/ChartContent';
import ListSectionContainer from '../shared/ListSectionContainer';
import ChartListItem from '../shared/ChartListItem';
import ChartListItemTitle from '../shared/ChartListItemTitle';
import ChartListCategoryTitle from '../shared/ChartListCategoryTitle';
import ChartListItemValue from '../shared/ChartListItemValue';
import VerticalDivider from '../shared/VerticalDivider';

const OrdersItemList = ({
  selectedCategory,
  hotel,
  timeframe,
  categories = [],
  withCategoryTitle = false,
}: {
  selectedCategory: CategoryRecord | null;
  hotel: HotelRecord;
  timeframe: Timeframe;
  categories?: CategoryRecord[];
  withCategoryTitle?: boolean;
}) => {
  const intl = useIntl();
  const {
    data: orderItemsByCategoryData = [],
    loading: isOrderItemsByCategoryLoading,
  } = useOrderOrderItemsByCategoryData({ hotel, timeframe, selectedCategory });

  const totalCountByItemId = orderItemsByCategoryData
    .flatMap(({ orderItems }) => orderItems)
    .reduce((acc: { [key: number]: number }, val) => {
      if (!acc[val.item_id]) {
        acc[val.item_id] = 0;
      }

      acc[val.item_id] += 1;

      return acc;
    }, {});
  const itemIds = Object.keys(totalCountByItemId);

  const {
    data: items = [],
    isLoading: itemsLoading,
  } = useGetMany<ItemRecordLocalized>(
    'items',
    { ids: itemIds },
    {
      enabled: !!itemIds.length,
    }
  );

  const isLoading = itemsLoading || isOrderItemsByCategoryLoading;

  // See https://github.com/marmelab/react-admin/issues/5054
  const itemData = items
    .filter((item) => item !== undefined)
    .map(({ id, title, categoryId }) => {
      const categoryRecord = categories.find(
        ({ id: catId }) => catId === categoryId
      );

      return {
        id,
        title,
        categoryTitle: categoryRecord?.title,
        total: totalCountByItemId[id as number],
      };
    });

  const sortedItems = itemData.sort((a, b) => b.total - a.total);
  const maxOrder = Math.max(...sortedItems.map(({ total }) => total));

  if (isLoading) {
    return <ChartsCircularProgress />;
  }

  return (
    <ChartList>
      {sortedItems.map((orderItem) => (
        <ChartListItem key={orderItem.id}>
          <ChartTooltip title={orderItem.title}>
            <ChartListItemTitle>{orderItem.title}</ChartListItemTitle>
          </ChartTooltip>
          {withCategoryTitle && (
            <ChartTooltip title={orderItem.categoryTitle || ''}>
              <ChartListCategoryTitle>
                {orderItem.categoryTitle}
              </ChartListCategoryTitle>
            </ChartTooltip>
          )}
          <ChartListItemValue>
            {intl.formatNumber(orderItem.total)}
          </ChartListItemValue>
          <ListItemProgressBar max={maxOrder} value={orderItem.total} />
        </ChartListItem>
      ))}
    </ChartList>
  );
};

const OrdersCategoryList = ({
  categories,
  hotel,
  timeframe,
  onClick,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
  onClick: Function;
}) => {
  const t = useTranslate();
  const intl = useIntl();

  const { data: orderByCategoryData, loading } = useOrderByCategoryData({
    hotel,
    timeframe,
  });

  const folders = orderByCategoryData.length
    ? orderByCategoryData
        .map(({ categoryId, result }) => {
          const categoryRecord = categories.find(
            ({ id }) => +categoryId === id
          );

          return {
            id: categoryId,
            title: categoryRecord
              ? categoryRecord.title
              : t('charts.deleted-folder', { id: categoryId }),
            total: result,
            category: categoryRecord,
          };
        })
        .sort((a, b) => b.total - a.total)
    : [];

  const maxOrder = Math.max(...folders.map(({ total }) => total));

  if (loading) {
    return <ChartsCircularProgress />;
  }

  return (
    <ChartList>
      {folders.map((folder) => (
        <ChartListItem
          clickable
          key={folder.id}
          onClick={() => onClick(folder.category)}
          data-testid="order-list-item"
        >
          <ChartTooltip title={folder.title}>
            <ChartListItemTitle>{folder.title}</ChartListItemTitle>
          </ChartTooltip>
          <ChartListItemValue>
            {intl.formatNumber(folder.total)}
          </ChartListItemValue>
          <KeyboardArrowRightIcon fontSize="small" />
          <ListItemProgressBar max={maxOrder} value={folder.total} />
        </ChartListItem>
      ))}
    </ChartList>
  );
};

const ListSection = ({
  categories,
  hotel,
  timeframe,
  selectedCategory,
  onClick,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
  selectedCategory: CategoryRecord | null;
  onClick: Function;
}) => {
  const t = useTranslate();
  const [currentListTab, setCurrentListTab] = useState('1');

  const onCurrentListTabChange = (
    _event: ChangeEvent<{}>,
    newValue: string
  ) => {
    setCurrentListTab(newValue);
  };

  return (
    <ListSectionContainer>
      <TabContext value={currentListTab}>
        <ChartListTabList onChange={onCurrentListTabChange}>
          {selectedCategory ? (
            <ChartListTab
              value="1"
              label={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                  <VerticalDivider style={{ margin: '6px 10px 6px 5px' }} />
                  {selectedCategory.title}
                </div>
              }
              onClick={() => onClick(null)}
            />
          ) : (
            [
              <ChartListTab
                key={0}
                value="1"
                label={t('charts.category-options.all-folders')}
              />,
              <VerticalDivider key={1} style={{ margin: '24px 16px' }} />,
              <ChartListTab
                key={2}
                value="2"
                label={t('charts.category-options.all-items')}
              />,
            ]
          )}
        </ChartListTabList>
        <ChartTabPanel value="1">
          {selectedCategory ? (
            <OrdersItemList
              selectedCategory={selectedCategory}
              hotel={hotel}
              timeframe={timeframe}
            />
          ) : (
            <OrdersCategoryList
              categories={categories}
              hotel={hotel}
              timeframe={timeframe}
              onClick={onClick}
            />
          )}
        </ChartTabPanel>
        <ChartTabPanel value="2">
          <OrdersItemList
            withCategoryTitle
            selectedCategory={null}
            categories={categories}
            hotel={hotel}
            timeframe={timeframe}
          />
        </ChartTabPanel>
      </TabContext>
    </ListSectionContainer>
  );
};

const OrderTotalsChartOrder = ({
  categories,
  hotel,
  timeframe,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
}) => {
  const t = useTranslate();
  const [currentChartTab, setCurrentChartTab] = useState('1');

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState<CategoryRecord | null>(null);

  const {
    chartProps: orderTotalsDataChartProps,
    loading: isOrderTotalsLoading,
  } = useOrderTotalsData({ hotel, timeframe, selectedCategory });

  const {
    chartProps: orderWeekdayAverageDataChartProps,
    loading: isOrderWeekdayAverageDataLoading,
  } = useOrderWeekdayAverageData({ hotel, timeframe, selectedCategory });

  const {
    chartProps: orderHourlyAverageDataChartProps,
    loading: isOrderHourlyAverageDataLoading,
  } = useOrderHourlyAverageData({ hotel, timeframe, selectedCategory });

  const onCurrentChartTabChange = (
    _event: ChangeEvent<{}>,
    newValue: string
  ) => {
    setCurrentChartTab(newValue);
  };

  const handleClick = (category: CategoryRecord | null) =>
    setSelectedCategory(category);

  return (
    <ChartContainer>
      <ChartSideList>
        <ListSection
          categories={categories}
          timeframe={timeframe}
          hotel={hotel}
          selectedCategory={selectedCategory}
          onClick={handleClick}
        />
      </ChartSideList>
      <ChartContent>
        <TabContext value={currentChartTab}>
          <ChartMainTabList onChange={onCurrentChartTabChange}>
            <ChartTab value="1" label={t('charts.order.chart-types.totals')} />
            <ChartTab
              value="2"
              label={t('charts.order.chart-types.weekday-average')}
            />
            <ChartTab
              value="3"
              label={t('charts.order.chart-types.hourly-average')}
            />
          </ChartMainTabList>
          <ChartTabPanel value="1">
            {isOrderTotalsLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...orderTotalsDataChartProps}
              />
            )}
          </ChartTabPanel>
          <ChartTabPanel value="2">
            {isOrderWeekdayAverageDataLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...orderWeekdayAverageDataChartProps}
              />
            )}
          </ChartTabPanel>
          <ChartTabPanel value="3">
            {isOrderHourlyAverageDataLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...orderHourlyAverageDataChartProps}
              />
            )}
          </ChartTabPanel>
        </TabContext>
      </ChartContent>
    </ChartContainer>
  );
};

export default OrderTotalsChartOrder;
